// src/App.tsx
import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { LoginView } from './Views/Login';
import Sidebar from './Components/Sidebar';
import { DeploymentFlow } from './Views/Deploy';
import { SiteDetailView } from './Views/ScrollListDetailView';
import { SitesListView } from './Views/ScrollListView';

import { ProfileView } from './Views/ProfileView';
import { StorageListView } from './Views/StorageListView';
import PublicLayout from './Components/PublicLayout';
import ProtectedLayout from './Components/ProtectedLayout';
import { PaymentResult } from './Components/PaymentSucessFailure';
// import { PlansPage } from './Components/PlansPageComponent';
import GitHubCallback from './Views/GitCallback';
import { BitbucketCallbackView } from './Views/BitBucketCallbackView';
import CloudAccountsView from './Views/CloudAccountsView';
import CostMonitorView from './Views/CostMonitorView';
import PreviewEnvironmentsView from './Views/PreviewEnvironmentView';
import TeamRBACView from './Views/TeamrbcView';
import { PlansPage } from './Views/BillingView';
import InfrastructureView from './Views/InfraStructure';
import { MonitoringDashboard } from './Views/MetricsDashboard';
import TeamInviteCallback from './Views/TeamsInviteCallback';



// ─── Error Boundary ───────────────────────────────────────────────────────────

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(_: Error) { return { hasError: true }; }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#09090e' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '20px', fontWeight: 700, color: '#eeeef5', marginBottom: '12px' }}>Something went wrong</div>
            <button
              onClick={() => this.setState({ hasError: false })}
              style={{ color: '#818cf8', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', textDecoration: 'underline' }}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Not Found ────────────────────────────────────────────────────────────────

const NotFound = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#09090e' }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '48px', fontWeight: 800, color: '#818cf8', marginBottom: '8px' }}>404</div>
      <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '18px', fontWeight: 700, color: '#eeeef5', marginBottom: '8px' }}>Page not found</div>
      <p style={{ fontSize: '13px', color: '#8e8ea8', marginBottom: '16px' }}>The page you're looking for doesn't exist.</p>
      <a href="/" style={{ color: '#818cf8', fontSize: '13px', textDecoration: 'underline' }}>Go back home</a>
    </div>
  </div>
);

// ─── App ──────────────────────────────────────────────────────────────────────

export const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route element={<PublicLayout />}>
            <Route path="/login" element={<LoginView />} />
            <Route path="/callback" element={<GitHubCallback />} />
            <Route path="/team/invite-callback" element={<TeamInviteCallback />} />
            <Route path="/bitbucket/callback" element={<BitbucketCallbackView />} />
          </Route>

          {/* Protected routes */}
          <Route element={<ProtectedLayout />}>
            {/* Deployments */}
            <Route path="/" element={<Navigate to="/sites" replace />} />
            <Route path="/sites" element={<SitesListView />} />
            <Route path="/sites/:id" element={<SiteDetailView />} />
            <Route path="/deploy" element={<DeploymentFlow />} />
            <Route path="/previews" element={<PreviewEnvironmentsView />} />

            {/* Infrastructure */}
            <Route path="/infrastructure" element={<InfrastructureView />} />
            <Route path="/cloud" element={<CloudAccountsView />} />
            {/* <Route path="/cost" element={<CostMonitorView />} /> */}

            {/* Account */}
            <Route path="/team" element={<TeamRBACView />} />
            <Route path="/profile" element={<ProfileView />} />
            <Route path="/plans" element={<PlansPage />} />
            {/* <Route path="/billing" element={<BillingView />} /> */}
            <Route path="/storage" element={<StorageListView />} />
            <Route path="/payment/result" element={<PaymentResult />} />
            <Route path="/monitoring" element={<MonitoringDashboard />} />
          </Route>

          {/* Catch all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;